import React, { useState, useRef, useEffect } from 'react';
import { FileUploadLoading } from './LoadingState';
import { FileFormatGuide } from './FileFormatGuide';
import { UploadArea } from './UploadArea';
import { isValidCSV, validateCSVContent } from '../../utils/fileValidation';
import { calculateEstimatedTime, formatTime } from '../../utils/timeEstimation';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { XCircle } from "lucide-react";

export default function FileUpload({
    file,
    setFile,
    setError,
    isAnalyzing,
    isLoading,
    analysisName,
    setAnalysisName
}) {
    const [isDragging, setIsDragging] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [tempName, setTempName] = useState('');
    const [estimatedTime, setEstimatedTime] = useState(null);
    const [validationErrors, setValidationErrors] = useState(null);
    const [headerError, setHeaderError] = useState(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const updateEstimatedTime = async () => {
            if (file) {
                const minutes = await calculateEstimatedTime(file);
                setEstimatedTime(formatTime(minutes));
            } else {
                setEstimatedTime(null);
            }
        };

        updateEstimatedTime();
    }, [file]);

    const validateAndSetFile = async (uploadedFile) => {
        if (!isValidCSV(uploadedFile)) {
            setError('Please upload a CSV file');
            setFile(null);
            return;
        }

        try {
            const validationResult = await validateCSVContent(uploadedFile);
            setFile(validationResult.normalizedFile);
            setAnalysisName(uploadedFile.name.replace('.csv', ''));
            setError('');
            setValidationErrors(null);
            setHeaderError(null);
        } catch (error) {
            if (error.type === 'header_error') {
                // Show header error dialog
                setHeaderError(error);
                setShowErrorDialog(true);
            } else if (error.errors) {
                // Show URL validation errors dialog
                setValidationErrors(error);
                setShowErrorDialog(true);
            } else {
                setError(error.message);
            }
            setFile(null);
        }
    };

    const handleFileUpload = (event) => {
        const uploadedFile = event.target.files[0];
        if (uploadedFile) {
            validateAndSetFile(uploadedFile);
        }
    };

    const startEditing = () => {
        setTempName(analysisName);
        setIsEditing(true);
    };

    const saveEdit = () => {
        const trimmedName = tempName.trim();
        if (trimmedName) {
            setAnalysisName(trimmedName.slice(0, 50));
        }
        setIsEditing(false);
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setTempName(analysisName);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveEdit();
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isAnalyzing) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        if (!isAnalyzing) {
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile) {
                validateAndSetFile(droppedFile);
            }
        }
    };

    const handleRetry = () => {
        setShowErrorDialog(false);
        // Reset file input to allow selecting the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (isLoading) {
        return <FileUploadLoading />;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
                <UploadArea
                    file={file}
                    isAnalyzing={isAnalyzing}
                    isDragging={isDragging}
                    estimatedTime={estimatedTime}
                    isEditing={isEditing}
                    analysisName={analysisName}
                    tempName={tempName}
                    startEditing={startEditing}
                    saveEdit={saveEdit}
                    cancelEdit={cancelEdit}
                    handleKeyPress={handleKeyPress}
                    setTempName={setTempName}
                    inputRef={inputRef}
                    fileInputRef={fileInputRef}
                    handleFileUpload={handleFileUpload}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    handleDrop={handleDrop}
                />
                <FileFormatGuide />
            </div>

            {/* Error Dialog */}
            <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 mb-2">
                            <XCircle className="h-5 w-5 text-destructive" />
                            {headerError ? 'Invalid CSV Structure' : 'Invalid URLs Found'}
                        </DialogTitle>
                        <DialogDescription>
                            {headerError ? (
                                <div className="space-y-2">
                                    <p>Your CSV file must have a column named "url".</p>
                                    <p>Found columns: <span className="font-mono">{headerError.details.foundColumns}</span></p>
                                    <p>Please rename your column to "url" and try again.</p>
                                </div>
                            ) : (
                                `Found ${validationErrors?.invalidCount} invalid URLs out of ${validationErrors?.totalCount} total URLs.
                                Please correct the following errors:`
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4">
                        {headerError ? (
                            <div className="space-y-4">
                                <Alert>
                                    <AlertDescription>
                                        <div className="space-y-2">
                                            <p>Example of correct CSV format:</p>
                                            <pre className="bg-muted p-2 rounded-md">
                                                url{'\n'}
                                                https://example.com{'\n'}
                                                https://another-example.com
                                            </pre>
                                        </div>
                                    </AlertDescription>
                                </Alert>
                            </div>
                        ) : (
                            <ScrollArea className="h-[300px] rounded-md border p-4">
                                <div className="space-y-3">
                                    {validationErrors?.errors.map((error, index) => (
                                        <Alert key={index} variant="destructive">
                                            <AlertDescription>
                                                <div className="text-sm">
                                                    <span className="font-medium">Row {error.row}:</span>{' '}
                                                    <span className="font-mono">{error.url || '[empty]'}</span>
                                                    <br />
                                                    <span className="text-destructive">{error.message}</span>
                                                </div>
                                            </AlertDescription>
                                        </Alert>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                    </div>

                    <DialogFooter>
                        <Button onClick={handleRetry}>
                            Try Again
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}